import { useEffect, useState, useCallback } from "react";

// OBS WebSocket v5 client (runs in browser since OBS is local).
// Spec: https://github.com/obsproject/obs-websocket/blob/master/docs/generated/protocol.md
async function sha256base64(text) {
  const buf = new TextEncoder().encode(text);
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return btoa(String.fromCharCode(...new Uint8Array(hash)));
}

const obsStore = {
  ws: null,
  cfgKey: "",
  connected: false,
  scenes: [],
  currentScene: "",
  error: null,
  url: "",
  requests: {},
  reqCounter: 0,
  listeners: new Set(),
};

export const DEFAULT_OBS_CONFIG = { host: "127.0.0.1", port: 4455, password: "" };

function scalarConfigValue(value, keys = []) {
  if (value === null || value === undefined) return "";
  if (typeof value === "string" || typeof value === "number") return String(value).trim();
  if (typeof value === "object") {
    for (const key of keys) {
      if (value[key] !== undefined) return scalarConfigValue(value[key], keys);
    }
    if (value.value !== undefined) return scalarConfigValue(value.value, keys);
    if (value.label !== undefined) return scalarConfigValue(value.label, keys);
  }
  return "";
}

export function normalizeObsConfig(config = {}) {
  const host = scalarConfigValue(config.host, ["host"]) || DEFAULT_OBS_CONFIG.host;
  const rawPort = scalarConfigValue(config.port, ["port"]);
  const port = Number(rawPort || DEFAULT_OBS_CONFIG.port);
  return {
    host,
    port: Number.isFinite(port) && port > 0 && port <= 65535 ? port : DEFAULT_OBS_CONFIG.port,
    password: scalarConfigValue(config.password, ["password"]),
  };
}

function notify() {
  obsStore.listeners.forEach((listener) => listener({
    connected: obsStore.connected,
    scenes: obsStore.scenes,
    currentScene: obsStore.currentScene,
    error: obsStore.error,
    url: obsStore.url,
  }));
}

export function useObs(config = DEFAULT_OBS_CONFIG) {
  const { host, port, password } = normalizeObsConfig(config);
  const [connected, setConnected] = useState(false);
  const [scenes, setScenes] = useState([]);
  const [currentScene, setCurrentScene] = useState("");
  const [error, setError] = useState(null);
  const [url, setUrl] = useState("");
  const cfgKey = `${host}:${port}:${password ? "password" : "nopassword"}`;

  const send = useCallback((op, data) => {
    if (obsStore.ws && obsStore.ws.readyState === 1) {
      obsStore.ws.send(JSON.stringify({ op, d: data }));
    }
  }, []);

  const request = useCallback((requestType, requestData) => {
    return new Promise((resolve, reject) => {
      const id = `r${++obsStore.reqCounter}`;
      obsStore.requests[id] = { resolve, reject };
      send(6, { requestType, requestId: id, requestData });
      setTimeout(() => {
        if (obsStore.requests[id]) {
          delete obsStore.requests[id];
          reject(new Error("OBS request timeout"));
        }
      }, 5000);
    });
  }, [send]);

  const connect = useCallback(() => {
    obsStore.error = null;
    notify();
    try {
      if (obsStore.ws && obsStore.cfgKey === cfgKey && obsStore.ws.readyState <= 1) {
        return;
      }
      obsStore.ws?.close();
      const nextUrl = `ws://${host}:${port}`;
      obsStore.url = nextUrl;
      const ws = new WebSocket(nextUrl);
      obsStore.ws = ws;
      obsStore.cfgKey = cfgKey;
      ws.onopen = () => {};
      ws.onerror = () => {
        obsStore.error = `Could not reach OBS at ${nextUrl}. Check that OBS is open, WebSocket Server is enabled, and the host/port/password match.`;
        obsStore.connected = false;
        notify();
      };
      ws.onclose = () => {
        obsStore.connected = false;
        notify();
      };
      ws.onmessage = async (event) => {
        const msg = JSON.parse(event.data);
        if (msg.op === 0) {
          // Hello
          const auth = msg.d.authentication;
          let identifyData = { rpcVersion: msg.d.rpcVersion };
          if (auth) {
            const secret = await sha256base64(password + auth.salt);
            const authStr = await sha256base64(secret + auth.challenge);
            identifyData.authentication = authStr;
          }
          send(1, identifyData);
        } else if (msg.op === 2) {
          // Identified
          obsStore.connected = true;
          obsStore.error = null;
          localStorage.setItem("obsAutoConnect", "true");
          notify();
          try {
            const sceneList = await request("GetSceneList");
            obsStore.scenes = sceneList.sceneList?.scenes?.map(s => s.sceneName).reverse() || sceneList.scenes?.map(s => s.sceneName).reverse() || [];
            obsStore.currentScene = sceneList.currentProgramSceneName || "";
            notify();
          } catch (e) { /* noop */ }
        } else if (msg.op === 7) {
          // RequestResponse
          const { requestId, responseData, requestStatus } = msg.d;
          const pending = obsStore.requests[requestId];
          if (pending) {
            delete obsStore.requests[requestId];
            if (requestStatus.result) pending.resolve(responseData || {});
            else pending.reject(new Error(requestStatus.comment || "OBS error"));
          }
        } else if (msg.op === 5) {
          // Event
          if (msg.d.eventType === "CurrentProgramSceneChanged") {
            obsStore.currentScene = msg.d.eventData.sceneName;
            notify();
          }
        }
      };
    } catch (e) {
      obsStore.error = String(e);
      obsStore.connected = false;
      notify();
    }
  }, [host, port, password, cfgKey, request, send]);

  const disconnect = useCallback(() => {
    localStorage.setItem("obsAutoConnect", "false");
    obsStore.ws?.close();
    obsStore.ws = null;
    obsStore.connected = false;
    obsStore.scenes = [];
    obsStore.currentScene = "";
    obsStore.url = "";
    notify();
  }, []);

  const setScene = useCallback(async (sceneName) => {
    await request("SetCurrentProgramScene", { sceneName });
    obsStore.currentScene = sceneName;
    notify();
  }, [request]);

  useEffect(() => {
    const sync = (state) => {
      setConnected(state.connected);
      setScenes(state.scenes);
      setCurrentScene(state.currentScene);
      setError(state.error);
      setUrl(state.url);
    };
    obsStore.listeners.add(sync);
    sync(obsStore);
    return () => obsStore.listeners.delete(sync);
  }, []);

  return { connected, scenes, currentScene, error, url, connect, disconnect, request, setScene };
}
